import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { PageLayout } from "../components/PageLayout";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { format } from "date-fns";
import { pt } from "date-fns/locale";
import { 
  AlertTriangle,
  FileDown
} from "lucide-react";

const INCIDENCIA_LABELS = {
  saida_forcada: "Saída Forçada",
  entrada_duplicada: "Entrada Duplicada",
  saida_sem_entrada: "Saída sem Entrada"
};

export default function IncidenciasPage() {
  const [incidencias, setIncidencias] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchIncidencias();
  }, []);

  const fetchIncidencias = async () => {
    try {
      const response = await axios.get(`${API}/incidencias`);
      setIncidencias(response.data);
    } catch (error) {
      toast.error("Erro ao carregar incidências");
    } finally {
      setLoading(false);
    }
  };

  const exportPdf = async () => {
    try {
      const response = await axios.get(`${API}/incidencias/export-pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `incidencias_${format(new Date(), "yyyy-MM-dd")}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF exportado");
    } catch (error) {
      toast.error("Erro ao exportar PDF");
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "-";
    try {
      return format(new Date(dateStr), "dd/MM/yyyy HH:mm", { locale: pt });
    } catch {
      return dateStr;
    }
  };

  const headerAction = incidencias.length > 0 ? (
    <Button onClick={exportPdf} variant="outline" data-testid="export-pdf-btn">
      <FileDown className="w-4 h-4 mr-2" />
      Exportar PDF
    </Button>
  ) : null;

  return (
    <PageLayout title="Incidências" subtitle="Registo de ocorrências anómalas" headerAction={headerAction}>
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0047FF]"></div>
        </div>
      ) : incidencias.length === 0 ? (
        <Card className="border-[#E2E8F0]">
          <CardContent className="p-8 text-center">
            <AlertTriangle className="w-12 h-12 text-[#64748B] mx-auto mb-4" />
            <p className="text-[#64748B]">Nenhuma incidência registada</p>
            <p className="text-sm text-[#64748B] mt-2">As incidências são geradas automaticamente quando ocorrem situações anómalas.</p>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-[#E2E8F0] overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Data/Hora</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Matrícula</TableHead>
                  <TableHead>Empresa</TableHead>
                  <TableHead>Local</TableHead>
                  <TableHead>Operador</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {incidencias.map((inc) => (
                  <TableRow key={inc.id} data-testid={`incidencia-row-${inc.id}`}>
                    <TableCell>{formatDateTime(inc.data_hora)}</TableCell>
                    <TableCell>
                      <Badge variant="destructive" className="bg-[#D50000]">
                        {INCIDENCIA_LABELS[inc.tipo_incidencia] || inc.tipo_incidencia}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-bold tracking-wider">{inc.matricula}</TableCell>
                    <TableCell>{inc.empresa_nome}</TableCell>
                    <TableCell>{inc.local_nome}</TableCell>
                    <TableCell>{inc.operador_nome}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}
    </PageLayout>
  );
}
